<template lang="html">
  <div class="results container">
    <div v-if="loading" class="spinner"></div>

    <template v-else>
      <start-window
        :vacancy-results="!!vacancies.length"
        :response-results="!!responses.length"
        :invite-results="!!invites.length"
        :search-query="searchQuery"
      />

      <div v-if="vacancies && vacancies.length > 0" class="list">
        <div class="results__inner-title">
          <div class="hint" @click="changeVacanciesVisibility">
            <div
              v-if="vacancies.length > 0"
              class="results__triangle"
              :class="
                isFoundedVacanciesShow
                  ? 'results__triangle_not-selected'
                  : 'results__triangle_selected'
              "
            ></div>
            <span>{{ vacanciesHint }}</span>
          </div>

          <action-panel-vacancies
            v-if="selectedVacancies"
            :list-length="selectedVacancies.length"
            :is-elements-selected="isVacanciesSelected"
            :archive-action="archiveVacancies"
            :remove-action="removeVacancy"
          />
        </div>

        <vacancies v-if="isFoundedVacanciesShow && vacancies.length" :list="vacancies" />
      </div>

      <div v-if="responses && responses.length > 0" class="list">
        <div class="results__inner-title">
          <div class="hint" @click="changeCandidatesVisibility">
            <div
              v-if="responses.length > 0"
              class="results__triangle"
              :class="
                isFoundedCandidatesShow
                  ? 'results__triangle_not-selected'
                  : 'results__triangle_selected'
              "
            ></div>
            <span>{{ responsesHint }}</span>
          </div>
          <action-panel-responses
            :list-length="selectedResponses.length"
            :is-elements-selected="isResponsesSelected"
            :export-action="exportResponses"
            :send-message-action="changeSendMessageDisplay"
            :link-share-action="changeLinkShareDisplay"
          />
        </div>

        <responses v-if="isFoundedVacanciesShow && responses.length" :list="responses" />
      </div>

      <div v-if="invites && invites.length > 0" class="list">
        <div class="results__inner-title">
          <div class="hint" @click="changeInvitesVisibility">
            <div
              v-if="invites.length"
              class="results__triangle"
              :class="
                isFoundedInvitesShow
                  ? 'results__triangle_not-selected'
                  : 'results__triangle_selected'
              "
            ></div>
            <span>{{ invitesHint }}</span>
          </div>
          <action-panel-invites :export-action="exportInvites" />
        </div>
        <invites v-if="isFoundedVacanciesShow && invites.length" :list="invites" />
      </div>
    </template>

    <message-send
      v-if="isMessageSendShown"
      :responses="responses"
      :selected-responses="selectedResponses"
      :selected-responses-ids="selectedResponses.map(response => response.id)"
      :change-display="changeSendMessageDisplay"
    />

    <link-share
      v-if="isLinkShareShown"
      :selected-responses-ids="selectedResponses.map(response => response.id)"
      :responses="selectedResponses"
      :change-display="changeLinkShareDisplay"
    />
  </div>
</template>

<script>
  import StartWindow from './Start.vue';
  import ActionPanelVacancies from './ActionPanelVacancies.vue';
  import ActionPanelResponses from './ActionPanelResponses.vue';
  import ActionPanelInvites from './ActionPanelInvites.vue';

  import Vacancies from './Vacancies.vue';
  import Responses from './Responses.vue';
  import Invites from './Invites.vue';

  import MessageSend from '@/components/modals/MessageSend.vue';
  import LinkShare from '@/components/modals/LinkShare.vue';

  import Pluralize from '@/mixins/pluralize';

  import { mapGetters, mapActions, mapState } from 'vuex';

  const isNotNull = v => v !== undefined && v !== null;

  export default {
    name: 'SearchResult',

    components: {
      StartWindow,
      ActionPanelVacancies,
      ActionPanelResponses,
      ActionPanelInvites,
      MessageSend,
      LinkShare,
      Vacancies,
      Responses,
      Invites
    },

    mixins: [Pluralize],

    data() {
      return {
        isMessageSendShown: false,
        isLinkShareShown: false,
        isFoundedVacanciesShow: true,
        isFoundedCandidatesShow: true,
        isFoundedInvitesShow: true
      };
    },

    computed: {
      ...mapState({
        selectedVacancies: 'vacancies/selected_list'
      }),

      ...mapGetters({
        searchQuery: 'search/searchQueryForResultPage',
        responses: 'search/responses/list',
        vacancies: 'search/vacancies/list',
        invites: 'search/invites/list',
        response_paginations: 'search/responses/pagination',
        vacancies_pagination: 'search/vacancies/pagination',
        invites_pagination: 'search/invites/pagination',
        loadingVacancies: 'search/vacancies/loading',
        loadingResponses: 'search/responses/loading',
        loadingInvites: 'search/invites/loading'
      }),

      searchTerm() {
        return this.$store.state.search.term;
      },

      vacanciesHint() {
        const count = this.vacancies_pagination
          ? this.vacancies_pagination.totalCounts
          : this.vacancies.length;

        if (!isNotNull(count)) {
          return '';
        }

        const pluralized = this.pluralize(this.$tr('vacancies.vacancy'), count);

        return `${count} ${pluralized}`;
      },

      responsesHint() {
        const count = this.responses_pagination
          ? this.responses_pagination.totalCounts
          : this.responses.length;

        if (!isNotNull(count)) {
          return '';
        }

        const pluralized = this.pluralize(this.$tr('responses.candidate'), count);

        return `${count} ${pluralized}`;
      },

      invitesHint() {
        const count = this.invites_pagination
          ? this.invites_pagination.totalCounts
          : this.invites.length;

        if (!isNotNull(count)) {
          return '';
        }

        const pluralized = this.pluralize(this.$tr('invites.inviteHint'), count);

        return `${count} ${pluralized}`;
      },

      selectedResponses() {
        // TODO: Maybe move selected to search state.
        // Current behaviour is dangerous
        // (go to the responses state from search)
        return this.$store.getters['responses/selectedList'].filter(response => {
          for (let i = 0; i < this.responses.length; i++) {
            if (this.responses[i].id === response.id) {
              return true;
            }
          }
        });
      },

      isVacanciesSelected() {
        console.log(this.selectedVacancies);
        return this.selectedVacancies.length > 0;
      },

      isResponsesSelected() {
        return this.selectedResponses.length > 0;
      },

      loading() {
        return this.loadingInvites || this.loadingResponses || this.loadingVacancies;
      }
    },

    methods: {
      ...mapActions('vacancies', [
        'setSelectedList',
        'updateVacancies',
        'fetchVacanciesListDropdown',
        'removeVacancies'
      ]),

      changeVacanciesVisibility() {
        this.isFoundedVacanciesShow = !this.isFoundedVacanciesShow;
      },

      changeCandidatesVisibility() {
        this.isFoundedCandidatesShow = !this.isFoundedCandidatesShow;
      },

      changeInvitesVisibility() {
        this.isFoundedInvitesShow = !this.isFoundedInvitesShow;
      },

      changeSendMessageDisplay() {
        this.isMessageSendShown = !this.isMessageSendShown;
      },

      changeLinkShareDisplay() {
        this.isLinkShareShown = !this.isLinkShareShown;
      },

      exportResponses() {
        // selected responses
        if (this.selectedResponses && this.selectedResponses.length > 0) {
          this.$store.dispatch(
            'search/xlsExportResponses',
            this.selectedResponses.map(resp => resp.id)
          );
          // or all responses on the page
        } else {
          this.$store.dispatch(
            'search/xlsExportResponses',
            this.responses.map(resp => resp.id)
          );
        }
      },

      exportInvites() {
        this.$store.dispatch('search/xlsExportInvites', this.searchQuery);
      },

      archiveVacancies() {
        const uncheckVacancy = vacancy => this.setSelectedList({ id: vacancy.id, value: false });
        const vacanciesToArchive = this.selectedVacancies.map(vacancy => ({
          id: vacancy.id,
          is_active: false
        }));

        this.updateVacancies(vacanciesToArchive)
          .then(() => this.fetchVacanciesListDropdown())
          .then(() =>
            this.$store.dispatch('search/vacancies/fetch', { searchTerm: this.searchTerm })
          )
          .then(() => this.selectedVacancies.forEach(vacancy => uncheckVacancy(vacancy)))
          .catch(err => this.$store.dispatch('global/setError', err));
      },

      removeVacancy() {
        if (this.isVacanciesSelected) {
          this.removeVacancies(this.selectedVacancies).then(() =>
            this.$store.dispatch('search/vacancies/fetch', { searchTerm: this.searchTerm })
          );
        }
      },

      removeResponse() {
        if (this.isResponsesSelected) {
          this.$store
            .dispatch('responses/remove', this.selectedResponses)
            .then(() =>
              this.$store.dispatch('search/responses/fetch', { searchTerm: this.searchTerm })
            );
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .results {
    padding-top: 20px;

    .spinner {
      margin: 0 auto;
      height: 50px;
      width: 50px;
      margin-top: 200px;
    }

    &__triangle {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 7px solid #b5b5b5;
      transition: all 0.3s;
      margin-right: 10px;

      &_not-selected {
        transform: rotate(180deg);
      }

      &_selected {
        transform: rotate(0deg);
      }
    }

    &__inner-title {
      position: relative;
      display: flex;
      font-size: 16px;
      align-items: center;
      padding-bottom: 10px;
      min-height: 40px;

      .hint {
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;

        span:first-child {
          margin-left: 22px;
        }
      }
    }

    &__inner-content {
      overflow-y: auto;
      overflow-x: hidden;
    }

    &__candidates-panel {
      display: flex;
      align-items: center;
      flex-direction: row-reverse;
      justify-content: flex-end !important;
      margin-bottom: 15px;
      margin-right: 15px;
    }

    &__candidates-content,
    &__vacancies-content {
      position: relative;
    }

    &__candidates-content .vcv-table__inner {
      overflow-y: auto;
      height: 400px;
      display: block;
    }

    .list + .list {
      margin-top: 20px;
    }
  }
</style>
